import { Ellipsis } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useTranslations } from 'next-intl';
import { useTheme } from 'next-themes';
import styled from 'styled-components';
import LanguageSwitcher, { rememberLocale, useLocaleLinks } from '@/components/language-switcher';
import ThemeToggle, { THEME_OPTIONS } from '@/components/theme-toggle';
import {
  Button,
  Logo,
  Menu,
  MenuItem,
  MenuLabel,
  MenuSeparator,
  SectionContainer,
} from '@/components/ui';
import statuses from '@/statuses';
import { useStore } from '@/stores/store-provider';
import { up } from '@/themes';

const StatusWidget = dynamic(() => import('./checkout-flow/status-widget'), { ssr: false });
const CartButton = dynamic(() => import('./cart-button'), { ssr: false });
const SupportButton = dynamic(() => import('./support-button'), { ssr: false });

export const BUSINESS_URL = 'https://www.bidali.com';

const Header = styled.header`
  -webkit-backdrop-filter: blur(12px);
  backdrop-filter: blur(12px);
  /*
   * Solid first: a browser without color-mix() (Safari < 16.2, Chrome < 111) drops
   * the next declaration, and the bar must never be see-through over the page.
   */
  background: var(--background);
  background: color-mix(in oklab, var(--background) 75%, transparent);
  border-bottom: 1px solid var(--border);
  border-bottom: 1px solid color-mix(in oklab, var(--border) 60%, transparent);
  box-sizing: border-box;
  color: var(--foreground);
  height: var(--nav-height);
  position: sticky;
  top: 0;
  width: 100%;
  z-index: 50;

  /* Without backdrop blur the bar needs to be nearly opaque to stay legible. */
  @supports not ((backdrop-filter: blur(12px)) or (-webkit-backdrop-filter: blur(12px))) {
    background: color-mix(in oklab, var(--background) 96%, transparent);
  }
`;

/* First focusable element on the page; only visible while focused. */
const SkipLink = styled.a`
  background: var(--primary);
  border-radius: var(--radius-lg);
  color: var(--primary-foreground);
  font-size: 14px;
  font-weight: 500;
  left: 12px;
  padding: 8px 12px;
  position: absolute;
  top: 12px;
  transform: translateY(-200%);
  z-index: 70;

  &:focus {
    transform: none;
  }
`;

const Inner = styled(SectionContainer)`
  align-items: center;
  display: flex;
  gap: 8px;
  height: 100%;

  ${up.sm} {
    gap: 16px;
  }
`;

const LogoLink = styled(Link)`
  border-radius: var(--radius-sm);
  display: inline-flex;
  flex-shrink: 0;
`;

const ToolbarContainer = styled.div`
  flex: 1;
  min-width: 0;
`;

const Right = styled.div`
  align-items: center;
  display: flex;
  flex-shrink: 0;
  gap: 4px;
`;

/* Language, theme and business link: separate controls from 640px up… */
const Wide = styled.div`
  align-items: center;
  display: none;
  gap: 4px;

  ${up.sm} {
    display: flex;
  }
`;

/* …and one "more" menu below that, so help and cart always fit at 360px. */
const Narrow = styled.div`
  display: flex;

  ${up.sm} {
    display: none;
  }
`;

const BusinessLink = styled(Button)`
  display: none;

  ${up.md} {
    display: inline-flex;
  }
`;

const MoreMenu = () => {
  const t = useTranslations('nav');
  const tT = useTranslations('theme');
  const lT = useTranslations('languages');
  const { forcedTheme, setTheme, theme } = useTheme();
  const { current, locales, hrefFor, refresh } = useLocaleLinks();

  return (
    <Menu
      onOpenChange={(open) => open && refresh()}
      renderTrigger={(triggerProps) => (
        <Button
          $size="icon"
          $variant="ghost"
          aria-label={t('more')}
          title={t('more')}
          type="button"
          {...triggerProps}
        >
          <Ellipsis size={20} strokeWidth={1.75} />
        </Button>
      )}
    >
      <MenuItem href={BUSINESS_URL}>{t('forBusiness')}</MenuItem>
      {!forcedTheme && (
        <>
          <MenuSeparator />
          <MenuLabel>{tT('title')}</MenuLabel>
          {THEME_OPTIONS.map(({ value, Icon }) => (
            <MenuItem
              checked={theme === value}
              icon={<Icon size={16} strokeWidth={1.75} />}
              key={value}
              onSelect={() => setTheme(value)}
            >
              {tT(value)}
            </MenuItem>
          ))}
        </>
      )}
      <MenuSeparator />
      <MenuLabel>{lT('language')}</MenuLabel>
      {locales.map((locale) => (
        <MenuItem
          checked={locale === current}
          href={hrefFor(locale)}
          hrefLang={locale}
          key={locale}
          lang={locale}
          onSelect={() => rememberLocale(locale)}
        >
          {lT(locale)}
        </MenuItem>
      ))}
    </Menu>
  );
};

/*
 * The checkout keeps its header to logo, order status, help and cart. A language
 * switch is a full navigation to /xx/checkout, which drops the order, charge and
 * quote held in memory and sends a shopper on step 2 or 3 back to step 1, so the
 * switcher is not offered there (nor is the business link, which leaves the site).
 * The theme toggle is also omitted here so the order flow stays focused; checkout
 * still follows the site theme chosen elsewhere.
 */
export const isCheckoutRoute = (pathname) => pathname === '/checkout';

const NavBar = () => {
  const router = useRouter();
  const store = useStore();
  const t = useTranslations('nav');
  const showSiteControls = !isCheckoutRoute(router.pathname);
  const shouldShowStatusWidget =
    router.isReady &&
    !router.pathname.includes('checkout') &&
    store.charge &&
    (store.charge.statusCode >= statuses.processing || store.paymentSubmitted);

  const handleClickStatus = () => {
    router.push('/checkout');
  };

  return (
    <Header data-section="nav">
      <SkipLink href="#main">{t('skipToContent')}</SkipLink>
      <Inner as="nav" aria-label={t('main')}>
        {/* The home page of the current locale: next/link adds the locale prefix. */}
        <LogoLink href="/" aria-label={t('home')}>
          <Logo />
        </LogoLink>
        <ToolbarContainer id="main-toolbar" />
        {shouldShowStatusWidget && (
          <StatusWidget store={store} onClick={handleClickStatus} onDismiss={store.clearCart} />
        )}
        <Right>
          {showSiteControls && (
            <>
              <Wide>
                <LanguageSwitcher />
                <ThemeToggle />
                <BusinessLink $size="lg" $variant="ghost" href={BUSINESS_URL}>
                  {t('forBusiness')}
                </BusinessLink>
              </Wide>
              <Narrow>
                <MoreMenu />
              </Narrow>
            </>
          )}
          <SupportButton />
          <CartButton />
        </Right>
      </Inner>
    </Header>
  );
};

export default observer(NavBar);
